/**
 * PLP Product Swatches - Collection Page Swatch Interactions
 * Works with Boost AI Search & Discovery product cards
 */

(function() {
  'use strict';
  
  const isMobile = () => window.innerWidth <= 1024;
  
  // Dynamic swatch loading - fetches missing color groups via Section Rendering API
  const pendingGroups = new Map();
  const loadingGroups = new Set();
  const failedGroups = new Set();
  let fetchBatchTimeout = null;

  async function fetchColorGroup(productHandle, groupHandle) {
    if (loadingGroups.has(groupHandle) || failedGroups.has(groupHandle)) return;
    loadingGroups.add(groupHandle);

    try {
      const res = await fetch(`/products/${productHandle}?sections=color-group-loader`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const html = data['color-group-loader'] || '';

      // Parse the JSON from rendered section
      const match = html.match(/<script[^>]*data-color-group[^>]*>([\s\S]*?)<\/script>/);
      if (match) {
        const groupData = JSON.parse(match[1].trim());
        if (groupData && groupData.colors && groupData.colors.length > 1) {
          window.plpColorGroups = window.plpColorGroups || {};
          window.plpColorGroups[groupHandle] = groupData;
          // Re-process queued products now that we have data
          const queued = pendingGroups.get(groupHandle) || [];
          pendingGroups.delete(groupHandle);
          queued.forEach(item => {
            item.dataset.swatchesPending = '';
            processBoostProduct(item);
          });
          return;
        }
      }

      failedGroups.add(groupHandle);
    } catch (e) {
      failedGroups.add(groupHandle);
    } finally {
      loadingGroups.delete(groupHandle);
      // Continue processing remaining queued groups
      if (pendingGroups.size > 0) setTimeout(processFetchQueue, 50);
    }
  }

  function queueDynamicLoad(productItem, groupHandle) {
    if (failedGroups.has(groupHandle)) return;

    if (!pendingGroups.has(groupHandle)) {
      pendingGroups.set(groupHandle, []);
    }
    pendingGroups.get(groupHandle).push(productItem);
    productItem.dataset.swatchesPending = 'true';

    // Batch fetches with a short delay so multiple products get grouped
    clearTimeout(fetchBatchTimeout);
    fetchBatchTimeout = setTimeout(processFetchQueue, 150);
  }

  function processFetchQueue() {
    const MAX_CONCURRENT = 3;
    let active = 0;

    for (const [groupHandle, items] of pendingGroups.entries()) {
      if (active >= MAX_CONCURRENT) break;
      if (loadingGroups.has(groupHandle) || failedGroups.has(groupHandle)) continue;

      // Use first queued product's handle for the API call
      let productHandle = null;
      try {
        const productData = JSON.parse(items[0].dataset.product);
        productHandle = productData.handle;
      } catch (e) { continue; }

      if (productHandle) {
        fetchColorGroup(productHandle, groupHandle);
        active++;
      }
    }
  }

  // Initialize swatches for all Boost AI product cards
  function initBoostSwatches() {
    const productItems = document.querySelectorAll('.boost-sd__product-item[data-product]');
    productItems.forEach(processBoostProduct);
  }
  
  function processBoostProduct(productItem) {
    // Skip if already processed or waiting for dynamic load
    if (productItem.dataset.swatchesProcessed === 'true') return;
    if (productItem.dataset.swatchesPending === 'true') return;
    
    // Parse product data from attribute
    let productData;
    try {
      productData = JSON.parse(productItem.dataset.product);
    } catch (e) {
      return;
    }
    
    // Find group tag
    const tags = productData.tags || [];
    let groupHandle = null;
    
    for (const tag of tags) {
      if (tag.toLowerCase().startsWith('group:')) {
        const rawValue = tag.replace(/^group:/i, '').trim();
        groupHandle = rawValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        break;
      }
    }
    
    if (!groupHandle) return;
    
    // Get color data from pre-rendered JSON
    const colorGroups = window.plpColorGroups || {};
    const groupData = colorGroups[groupHandle];
    
    if (!groupData || !groupData.colors || groupData.colors.length < 2) {
      queueDynamicLoad(productItem, groupHandle);
      return;
    }
    
    // Get current product handle
    const currentHandle = productData.handle;
    
    // Make sure current product actually belongs in this color group
    const currentInGroup = groupData.colors.some(c => c.handle === currentHandle);
    if (!currentInGroup) return;
    
    // Find and replace the meta-color-count element
    const colorCountEl = productItem.querySelector('meta-color-count, .swatches_count');
    if (!colorCountEl) return;
    
    // Build swatches HTML
    const swatchesHTML = buildSwatchesHTML(groupData.colors, currentHandle);
    
    // Create container and insert
    const swatchContainer = document.createElement('div');
    swatchContainer.className = 'plp-swatches';
    swatchContainer.dataset.productHandle = currentHandle;
    swatchContainer.innerHTML = swatchesHTML;
    
    colorCountEl.parentNode.replaceChild(swatchContainer, colorCountEl);
    
    initSwatchInteractions(swatchContainer, productItem);
    
    // Mark as processed
    productItem.dataset.swatchesProcessed = 'true';
  }
  
  // Normalize color name to match CSS selectors
  function normalizeColor(colorName) {
    if (!colorName) return 'grey';
    
    const colorLower = colorName.toLowerCase().trim();
    
    // Direct match with base colors first (exact or contains)
    const baseColors = ['black', 'white', 'red', 'blue', 'green', 'yellow', 
      'orange', 'pink', 'purple', 'brown', 'grey', 'gray', 'beige'];
    
    // Exact match first
    for (const base of baseColors) {
      if (colorLower === base) {
        return base === 'gray' ? 'grey' : base;
      }
    }
    
    // Check for multi-color only if explicitly contains "multi"
    if (colorLower.includes('multi')) {
      return 'multi';
    }
    
    // Contains match for base colors
    for (const base of baseColors) {
      if (colorLower.includes(base)) {
        return base === 'gray' ? 'grey' : base;
      }
    }
    
    // Color word mappings for common variations
    const colorMap = {
      'cream': 'beige', 'ivory': 'beige', 'tan': 'beige', 'khaki': 'beige', 'sand': 'beige', 'nude': 'beige', 'oat': 'beige', 'camel': 'beige',
      'navy': 'blue', 'cobalt': 'blue', 'azure': 'blue', 'royal': 'blue', 'denim': 'blue', 'indigo': 'blue',
      'burgundy': 'red', 'maroon': 'red', 'crimson': 'red', 'scarlet': 'red', 'cherry': 'red', 'wine': 'red',
      'olive': 'green', 'mint': 'green', 'sage': 'green', 'forest': 'green', 'emerald': 'green', 'lime': 'green', 'teal': 'green',
      'silver': 'grey', 'charcoal': 'grey', 'slate': 'grey', 'ash': 'grey', 'graphite': 'grey', 'smoke': 'grey',
      'gold': 'yellow', 'mustard': 'yellow', 'lemon': 'yellow',
      'coral': 'orange', 'peach': 'orange', 'apricot': 'orange', 'rust': 'orange', 'copper': 'orange', 'terracotta': 'orange',
      'magenta': 'pink', 'rose': 'pink', 'blush': 'pink', 'salmon': 'pink', 'fuchsia': 'pink',
      'lavender': 'purple', 'violet': 'purple', 'plum': 'purple', 'mauve': 'purple', 'lilac': 'purple',
      'chocolate': 'brown', 'coffee': 'brown', 'mocha': 'brown', 'espresso': 'brown', 'walnut': 'brown', 'chestnut': 'brown'
    };
    
    for (const [key, value] of Object.entries(colorMap)) {
      if (colorLower.includes(key)) {
        return value;
      }
    }
    
    return 'grey'; // Default fallback
  }

  function buildSwatchesHTML(colors, currentHandle) {
    // Find current product index
    let currentIndex = colors.findIndex(c => c.handle === currentHandle);
    if (currentIndex === -1) currentIndex = 0;
    
    // Reorder for mobile: current first
    const mobileColors = [...colors];
    if (currentIndex > 0) {
      const current = mobileColors.splice(currentIndex, 1)[0];
      mobileColors.unshift(current);
    }
    
    // Desktop: exclude current, show up to 3
    const desktopColors = colors.filter(c => c.handle !== currentHandle);
    const desktopVisible = desktopColors.slice(0, 3);
    const extraCount = desktopColors.length - 3;
    
    // Build desktop HTML
    let desktopHTML = '<div class="plp-swatches__desktop"><div class="plp-swatches__container">';
    desktopVisible.forEach(color => {
      const swatchColor = normalizeColor(color.color);
      desktopHTML += `
        <span class="plp-swatches__swatch" data-color="${color.color}" aria-label="${color.color}">
          <span class="plp-swatches__swatch-inner" data-swatch="${swatchColor}"></span>
        </span>`;
    });
    if (extraCount > 0) {
      desktopHTML += `<span class="plp-swatches__more">+</span>`;
    }
    desktopHTML += '</div></div>';
    
    // Build mobile HTML
    let mobileHTML = '<div class="plp-swatches__mobile"><div class="plp-swatches__container">';
    mobileColors.forEach((color, idx) => {
      const swatchColor = normalizeColor(color.color);
      // First swatch (idx 0) is always active since we reorder the array to put current product first
      const isActive = idx === 0 ? ' plp-swatches__swatch--active' : '';
      const priceRaw = color.priceRaw || 0;
      const comparePriceRaw = color.comparePriceRaw || 0;
      mobileHTML += `
        <button type="button" 
                class="plp-swatches__swatch${isActive}"
                data-color="${color.color}"
                data-product-url="${color.url}"
                data-product-image="${color.image}"
                data-product-price="${color.price}"
                data-product-price-raw="${priceRaw}"
                data-product-compare-price="${color.comparePrice || ''}"
                data-product-compare-price-raw="${comparePriceRaw}"
                data-product-handle="${color.handle}"
                aria-label="${color.color}">
          <span class="plp-swatches__swatch-inner" data-swatch="${swatchColor}"></span>
        </button>`;
    });
    mobileHTML += '</div>';
    
    // Always add scroll arrow - visibility will be controlled by JavaScript based on scrollability
    mobileHTML += `
      <button type="button" class="plp-swatches__scroll-arrow" aria-label="Scroll for more colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>`;
    mobileHTML += '</div>';
    
    return desktopHTML + mobileHTML;
  }
  
  function initSwatchInteractions(container, productItem) {
    const mobileContainer = container.querySelector('.plp-swatches__mobile');
    if (!mobileContainer) return;
    
    const swatchesWrapper = mobileContainer.querySelector('.plp-swatches__container');
    const swatches = mobileContainer.querySelectorAll('.plp-swatches__swatch');
    const scrollArrow = mobileContainer.querySelector('.plp-swatches__scroll-arrow');
    
    // Mobile swatch click handler
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSwatchClick(swatch, mobileContainer, productItem);
      });
    });
    
    // Scroll behavior - check if scrollable first
    if (swatchesWrapper) {
      // Check if arrow exists, if not create it
      if (!scrollArrow) {
        scrollArrow = mobileContainer.querySelector('.plp-swatches__scroll-arrow');
      }
      if (scrollArrow) {
        initScrollBehavior(swatchesWrapper, scrollArrow);
      }
    }
  }
  
  function handleSwatchClick(swatch, container, productItem) {
    // Update active state
    container.querySelectorAll('.plp-swatches__swatch').forEach(s => {
      s.classList.remove('plp-swatches__swatch--active');
    });
    swatch.classList.add('plp-swatches__swatch--active');
    
    // Get variant data
    const productUrl = swatch.dataset.productUrl;
    const productImage = swatch.dataset.productImage;
    const productPrice = swatch.dataset.productPrice;
    const productPriceRaw = parseFloat(swatch.dataset.productPriceRaw) || 0;
    const productComparePrice = swatch.dataset.productComparePrice;
    const productComparePriceRaw = parseFloat(swatch.dataset.productComparePriceRaw) || 0;
    
    // Update product image
    updateBoostProductImage(productItem, productImage);
    
    // Update product price and sale badge
    updateBoostProductPrice(productItem, productPrice, productComparePrice, productPriceRaw, productComparePriceRaw);
    
    // Update product links
    updateBoostProductLinks(productItem, productUrl);
  }
  
  function updateBoostProductImage(productItem, imageUrl) {
    if (!imageUrl) return;
    
    // Find Boost AI image elements
    const mainImg = productItem.querySelector('.boost-sd__product-image-img--main');
    const secondImg = productItem.querySelector('.boost-sd__product-image-img--second');
    
    if (mainImg) {
      mainImg.src = imageUrl;
      mainImg.srcset = generateSrcset(imageUrl);
    }
    
    if (secondImg) {
      // Hide second image when changing variants
      secondImg.style.opacity = '0';
    }
  }
  
  function generateSrcset(baseUrl) {
    const widths = [200, 300, 400, 500, 700];
    return widths.map(w => {
      let url = baseUrl;
      if (baseUrl.includes('width=')) {
        url = baseUrl.replace(/width=\d+/, `width=${w}`);
      } else if (baseUrl.includes('?')) {
        url = baseUrl + `&width=${w}`;
      } else {
        url = baseUrl + `?width=${w}`;
      }
      return `${url} ${w}w`;
    }).join(', ');
  }
  
  function updateBoostProductPrice(productItem, price, comparePrice, priceRaw, comparePriceRaw) {
    if (!price) return;
    
    const priceWrapper = productItem.querySelector('.boost-sd__product-price-wrapper');
    if (!priceWrapper) return;
    
    const isOnSale = comparePriceRaw > 0 && comparePriceRaw > priceRaw;
    
    // Calculate discount percentage
    let discountPercent = 0;
    if (isOnSale) {
      discountPercent = Math.round(((comparePriceRaw - priceRaw) / comparePriceRaw) * 100);
    }
    
    // Update sale badge
    updateSaleBadge(productItem, isOnSale, discountPercent);
    
    // Build new price HTML
    let priceHTML = '';
    if (isOnSale) {
      priceHTML = `
        <span class="boost-sd__visually-hidden">Sale price ${price}, was ${comparePrice}</span>
        <span aria-hidden="true" class="boost-sd__product-price-content boost-sd__product-price-content--text-align-left">
          <span class="boost-sd__product-price--sale" style="color: #c41e3a; font-weight: 600;">
            <span class="boost-sd__format-currency">${price}</span>
          </span>
          <span class="boost-sd__product-price--compare" style="text-decoration: line-through; color: #666; margin-left: 6px;">
            <span class="boost-sd__format-currency">${comparePrice}</span>
          </span>
        </span>`;
    } else {
      priceHTML = `
        <span class="boost-sd__visually-hidden">Regular price ${price}</span>
        <span aria-hidden="true" class="boost-sd__product-price-content boost-sd__product-price-content--text-align-left">
          <span class="boost-sd__product-price--default">
            <span class="boost-sd__format-currency">${price}</span>
          </span>
        </span>`;
    }
    
    priceWrapper.innerHTML = priceHTML;
  }
  
  function updateSaleBadge(productItem, isOnSale, discountPercent) {
    // Find or create the badge container in the image area
    const imageWrapper = productItem.querySelector('.boost-sd__product-image-wrapper');
    if (!imageWrapper) return;
    
    // Look for existing custom sale badge
    let saleBadge = productItem.querySelector('.plp-swatch-sale-badge');
    
    // Also get Boost's original sale badge
    const boostSaleBadge = productItem.querySelector('.boost-sd__product-label--sale');
    
    if (isOnSale && discountPercent > 0) {
      // Show Boost's badge and update its text
      if (boostSaleBadge) {
        const badgeText = boostSaleBadge.querySelector('.boost-sd__product-label-text span');
        if (badgeText) {
          badgeText.textContent = `Sale -${discountPercent}%`;
        }
        boostSaleBadge.style.display = '';
      }
      
      // Hide our custom badge if Boost's badge exists
      if (saleBadge && boostSaleBadge) {
        saleBadge.style.display = 'none';
      } else if (!boostSaleBadge) {
        // Create our badge only if Boost's doesn't exist
        if (!saleBadge) {
          saleBadge = document.createElement('div');
          saleBadge.className = 'plp-swatch-sale-badge';
          const topRow = imageWrapper.querySelector('.boost-sd__product-image-row--top');
          if (topRow) {
            topRow.insertAdjacentElement('afterbegin', saleBadge);
          } else {
            imageWrapper.insertAdjacentElement('afterbegin', saleBadge);
          }
        }
        saleBadge.textContent = `Sale -${discountPercent}%`;
        saleBadge.style.display = '';
      }
    } else {
      // Hide both badges if not on sale
      if (saleBadge) {
        saleBadge.style.display = 'none';
      }
      if (boostSaleBadge) {
        boostSaleBadge.style.display = 'none';
      }
    }
  }
  
  function updateBoostProductLinks(productItem, url) {
    if (!url) return;
    
    // Store the active swatch URL on the product item
    productItem.dataset.swatchUrl = url;
    
    // Update all product link hrefs
    const links = productItem.querySelectorAll('a.boost-sd__product-link, a.boost-sd__product-image');
    links.forEach(link => {
      link.href = url;
    });
    
    // Intercept clicks on the entire image wrapper so overlay divs don't block navigation
    const imageWrapper = productItem.querySelector('.boost-sd__product-image-wrapper');
    if (imageWrapper && !imageWrapper._swatchClickBound) {
      imageWrapper.addEventListener('click', function(e) {
        const activeUrl = productItem.dataset.swatchUrl;
        if (!activeUrl) return;
        e.preventDefault();
        e.stopPropagation();
        window.location.href = activeUrl;
      }, true); // capture phase to run before Boost's handler
      imageWrapper._swatchClickBound = true;
    }
  }
  
  function initScrollBehavior(wrapper, arrow) {
    if (!arrow) return;
    
    // Check if container is scrollable
    function checkScrollability() {
      const isScrollable = wrapper.scrollWidth > wrapper.clientWidth;
      if (isScrollable) {
        arrow.classList.add('is-visible');
        arrow.classList.remove('is-hidden');
      } else {
        arrow.classList.remove('is-visible');
        arrow.classList.add('is-hidden');
      }
      return isScrollable;
    }
    
    let scrollTimeout;
    
    wrapper.addEventListener('scroll', () => {
      wrapper.classList.add('is-scrolling');
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        wrapper.classList.remove('is-scrolling');
      }, 1000);
      
      updateArrowVisibility(wrapper, arrow);
    });
    
    arrow.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      wrapper.scrollBy({ left: 60, behavior: 'smooth' });
    });
    
    // Initial check for scrollability and position
    setTimeout(() => {
      const isScrollable = checkScrollability();
      if (isScrollable) {
        updateArrowVisibility(wrapper, arrow);
      }
    }, 100);
    
    // Re-check on resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const isScrollable = checkScrollability();
        if (isScrollable) {
          updateArrowVisibility(wrapper, arrow);
        }
      }, 100);
    });
  }
  
  function updateArrowVisibility(wrapper, arrow) {
    // Only update if arrow is visible (scrollable)
    if (!arrow.classList.contains('is-visible')) return;
    
    const isAtEnd = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 5;
    arrow.classList.toggle('is-hidden', isAtEnd);
  }
  
  // Initialize scroll behavior for all swatch containers (Liquid template)
  function initAllSwatchScrollBehavior() {
    const allSwatchContainers = document.querySelectorAll('.plp-swatches__mobile');
    allSwatchContainers.forEach(mobileContainer => {
      // Skip if already processed by Boost AI
      if (mobileContainer.closest('[data-swatches-processed="true"]')) return;
      
      const swatchesWrapper = mobileContainer.querySelector('.plp-swatches__container');
      const scrollArrow = mobileContainer.querySelector('.plp-swatches__scroll-arrow');
      
      if (swatchesWrapper && scrollArrow && !mobileContainer.dataset.scrollInit) {
        initScrollBehavior(swatchesWrapper, scrollArrow);
        mobileContainer.dataset.scrollInit = 'true';
      }
    });
  }
  
  function observeBoostProducts() {
    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;
      
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains('boost-sd__product-item')) {
              shouldInit = true;
            } else if (node.querySelector && node.querySelector('.boost-sd__product-item')) {
              shouldInit = true;
            }
          }
        });
      });
      
      if (shouldInit) {
        // Slight delay to let Boost finish rendering
        setTimeout(() => {
          initBoostSwatches();
          initAllSwatchScrollBehavior();
        }, 50);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  function init() {
    initBoostSwatches();
    observeBoostProducts();
    initAllSwatchScrollBehavior();
    // initAllCustomScrollbars();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  setTimeout(init, 500);
  setTimeout(init, 1000);
  setTimeout(init, 2000);
  
  // Re-init on resize
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      initBoostSwatches();
      initAllSwatchScrollBehavior();
    }, 250);
  });
  
  // Expose for external use
  window.PLPSwatches = {
    init: initBoostSwatches,
    processProduct: processBoostProduct
  };
})();
